import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { GraduationCap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { DISCLAIMER_SHORT, CONSENT_STORAGE_KEY } from "@/lib/disclaimers";
import {
  AGE_GATE_DOB_LABEL,
  AGE_GATE_CHECKBOX,
  AGE_GATE_ERROR,
  AGE_GATE_SUCCESS,
  AGE_GATE_HELPER,
} from "@/lib/legal-copy";
import {
  DateOfBirthPicker,
  dobToDate,
  calculateAge,
  type DOBValue,
} from "@/components/DateOfBirthPicker";
import { PrivacyBadge } from "@/components/PrivacyBadge";
export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Sign in or create your SmartEssay account" },
      { name: "description", content: "Sign in or create a free SmartEssay account to save your framed outlines, summaries, and key-concept maps." },
      { property: "og:title", content: "Sign in — SmartEssay" },
      { property: "og:description", content: "Sign in or create a free SmartEssay account." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [dob, setDob] = useState<DOBValue>({ year: null, month: null, day: null });
  const [confirmedAge, setConfirmedAge] = useState(false);
  const [ageError, setAgeError] = useState<string | null>(null);
  const [needsConfirm, setNeedsConfirm] = useState(false);

  const dobDate = dobToDate(dob);
  const ageOk = dobDate ? calculateAge(dobDate) >= 13 : false;
  const signupReady = ageOk && confirmedAge && acceptedTerms;

  function validateAgeGate(): boolean {
    if (!dobDate) {
      setAgeError("Please enter your full date of birth.");
      toast.error("Please enter your full date of birth.");
      return false;
    }
    if (calculateAge(dobDate) < 13) {
      setAgeError(AGE_GATE_ERROR);
      toast.error(AGE_GATE_ERROR);
      return false;
    }
    if (!confirmedAge) {
      setAgeError("Please confirm you are at least 13 years old.");
      toast.error("Please confirm you are at least 13 years old.");
      return false;
    }
    setAgeError(null);
    return true;
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: "/app" });
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (session) navigate({ to: "/app" });
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (mode === "signup") {
      if (!validateAgeGate()) return;
      if (!acceptedTerms) {
        toast.error("Please acknowledge the responsible-use notice to continue.");
        return;
      }
    }
    setLoading(true);
    try {
      if (mode === "signup") {
        try {
          window.localStorage.setItem(CONSENT_STORAGE_KEY, "1");
        } catch { /* ignore */ }
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/app`,
            data: {
              full_name: name,
              birth_year: dobDate ? dobDate.getFullYear() : null,
            },
          },
        });
        if (error) throw error;
        toast.success(AGE_GATE_SUCCESS);
        toast.success("Check your email to confirm your account.");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
          if (/confirm/i.test(error.message)) setNeedsConfirm(true);
          throw error;
        }
        navigate({ to: "/app" });
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function onResendConfirmation() {
    if (!email) {
      toast.error("Enter your email above first.");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: { emailRedirectTo: `${window.location.origin}/app` },
      });
      if (error) throw error;
      toast.success("Confirmation email sent. Check your inbox (and spam).");
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function onGoogle() {
    if (mode === "signup") {
      if (!validateAgeGate()) return;
      if (!acceptedTerms) {
        toast.error("Please acknowledge the responsible-use notice to continue.");
        return;
      }
    }
    if (mode === "signup") {
      try { window.localStorage.setItem(CONSENT_STORAGE_KEY, "1"); } catch { /* ignore */ }
    }
    setLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: `${window.location.origin}/app`,
      });
      if (result.error) {
        toast.error(result.error.message ?? "Google sign-in failed.");
        return;
      }
      if (result.redirected) return;
      navigate({ to: "/app" });
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-hero-gradient">
      <div className="mx-auto flex max-w-md flex-col px-6 py-10">
        <Link to="/" className="mb-10 inline-flex items-center gap-2 font-display text-xl font-semibold">
          <GraduationCap className="h-6 w-6 text-primary" /> SmartEssay
        </Link>
        <div className="rounded-2xl border border-border bg-card p-7 shadow-soft">
          <h1 className="font-display text-2xl font-semibold">
            {mode === "signup" ? "Create your account" : "Welcome back"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {mode === "signup" ? "Free to try. No card needed." : "Sign in to keep working."}
          </p>

          <Button
            type="button"
            variant="outline"
            className="mt-6 w-full"
            onClick={onGoogle}
            disabled={loading}
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.12A6.6 6.6 0 0 1 5.48 12c0-.74.13-1.45.36-2.12V7.04H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.96l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-wider text-muted-foreground">
            <span className="h-px flex-1 bg-border" /> or email <span className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            {mode === "signup" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Name</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Alex Rivera" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            {mode === "signup" && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="dob">{AGE_GATE_DOB_LABEL}</Label>
                  <DateOfBirthPicker id="dob" value={dob} onChange={(v) => { setDob(v); setAgeError(null); }} />
                  <p className="text-xs text-muted-foreground">{AGE_GATE_HELPER}</p>
                </div>
                <label
                  htmlFor="confirm-age"
                  className="flex cursor-pointer items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-3 text-xs leading-relaxed text-muted-foreground"
                >
                  <Checkbox
                    id="confirm-age"
                    checked={confirmedAge}
                    onCheckedChange={(v) => setConfirmedAge(v === true)}
                    className="mt-0.5"
                  />
                  <span>{AGE_GATE_CHECKBOX}</span>
                </label>
                {ageError && (
                  <p className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs font-medium text-destructive">
                    {ageError}
                  </p>
                )}
                <PrivacyBadge className="w-full justify-center" />
                <label
                  htmlFor="accept-terms"
                  className="flex cursor-pointer items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-3 text-xs leading-relaxed text-muted-foreground"
                >
                  <Checkbox
                    id="accept-terms"
                    checked={acceptedTerms}
                    onCheckedChange={(v) => setAcceptedTerms(v === true)}
                    className="mt-0.5"
                  />
                  <span>
                    {DISCLAIMER_SHORT}{" "}
                    <Link
                      to="/terms"
                      className="text-primary underline-offset-2 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Read full Terms
                    </Link>
                    .
                  </span>
                </label>
              </>
            )}
            <Button
              type="submit"
              className="w-full shadow-glow"
              disabled={loading || (mode === "signup" && !signupReady)}
            >
              {loading ? "..." : mode === "signup" ? "Create account" : "Sign in"}
            </Button>
          </form>
          {mode === "signin" && (
            <div className="mt-4 text-center text-sm">
              {needsConfirm && (
                <p className="mb-2 rounded-md border border-amber-400/40 bg-amber-50 p-2 text-xs text-amber-900">
                  Your email isn't confirmed yet. Resend the confirmation link below.
                </p>
              )}
              <button
                type="button"
                onClick={onResendConfirmation}
                disabled={loading}
                className="text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                Didn't get the confirmation email? Resend it
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={() => setMode(mode === "signup" ? "signin" : "signup")}
            className="mt-4 w-full text-center text-sm text-muted-foreground hover:text-foreground"
          >
            {mode === "signup" ? "Already have an account? Sign in" : "New here? Create an account"}
          </button>
        </div>
      </div>

    </div>
  );
}
